'use client';

import type { LegacyAnalyticsHandlers } from '@jouzen/web-analytics-store';
import { WebAnalyticsProvider } from '@jouzen/web-analytics-store/client';
import { useFeatureGate } from '@statsig/react-bindings';
import { env } from 'next-runtime-env';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { STATSIG_STABLE_ID_COOKIE_NAME } from '@/app/consts/cookies';
import { getCookie, setCookie } from '@/utils/cookie';
import { Analytics } from '@/utils/analytics';

const WEB_ANALYTICS_STORE_ROLLOUT_GATE =
  'web_analytics_store_bulk_order_portal_rollout';

function legacyPageHandler(
  pageName: string,
  properties?: Record<string, unknown>,
) {
  Analytics.trackEvent('page_view', {
    pathname: pageName,
    ...properties,
  });
}

export default function WebAnalyticsInit({
  children,
}: Readonly<PropsWithChildren>) {
  const { value: isStoreEnabled } = useFeatureGate(
    WEB_ANALYTICS_STORE_ROLLOUT_GATE,
  );

  const legacyHandlers = useMemo<LegacyAnalyticsHandlers>(
    () => ({
      track: (event, properties) => {
        if (event === 'button_click') {
          Analytics.trackEvent('button_click', properties);
        }
      },
      page: legacyPageHandler,
    }),
    [],
  );

  return (
    <WebAnalyticsProvider
      config={{
        debugEnabled: env('NEXT_PUBLIC_DEBUG_ANALYTICS') === 'true',
        getCookie: (name) => getCookie(name) ?? undefined,
        setCookie,
        isStoreEnabled: () => isStoreEnabled,
        legacyHandlers,
        statsigStableId: getCookie(STATSIG_STABLE_ID_COOKIE_NAME),
      }}
    >
      {children}
    </WebAnalyticsProvider>
  );
}
